import { useId, useState } from 'react'
import type { JSX } from 'react'

import { dioptre, formatDioptre, isRxReadAxis, type AiTask } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import {
  NotDecided,
  composeAnswer,
  contextFields,
  contextPatternOf,
  decisionFields,
  fieldState,
  proposalFields,
  rxNumber,
  rxPosition,
  undecidedCount,
  type ComposedAnswer,
  type Decision,
  type Decisions,
  type FieldProblem,
  type ProposalField,
} from './acceptance.js'

/**
 * `AI-001`'s acceptance screen: what Mira proposed, field by field, and one confirm at the end.
 *
 * > `AI-001` `MUST`: Everything Mira produces enters as a *proposal* that a person accepts, edits or
 * > rejects. A wrong sphere value is a remake; a wrong price is a dispute.
 *
 * **Nothing here is pre-selected.** A field opens undecided, and the confirm button stays inert until
 * every field has a valid decision. Pre-selecting "accept" would make the safe path the one that
 * needs effort and the unsafe path the one that happens when somebody presses Enter, which is the
 * wrong way round for a value that goes on a lens. There is no accept-all for the same reason:
 * "reject all" is offered because refusing is always safe, and accepting in bulk is how a misread
 * sphere reaches a lab beside seven correct values.
 *
 * **The confirm control is `aria-disabled`, not `disabled`.** A disabled button drops out of the tab
 * order, so a keyboard user reaches the end of the list and finds nothing there and no reason. An
 * `aria-disabled` one can be focused, announces that it is unavailable, and the line beside it says
 * how many values are left. The handler refuses as well, and {@link composeAnswer} throws, so the
 * attribute is a description of the rule and not the rule.
 *
 * Every control is a native radio, input or button, so the whole screen works from the keyboard
 * (`KB-02`): arrows move within a field's three choices, Tab moves between fields.
 *
 * **Not every leaf is a decision, and which ones are is data.** `AI-010` returns `readable`, the
 * script, the hand, a `legibility` per eye and a note beside the eight prescription values, and
 * `AI-001` is about values that get written: a legibility flag is not one. Those are shown first,
 * as a short read-only block, because they are what a person uses to judge the eight. The split
 * lives in `MIRA_CONTEXT_FIELDS` and is argued there; this screen renders both halves and
 * `acceptance.ts` makes sure a prescription value can never land in the wrong one. Twelve presses
 * where eight were needed is how a person is trained to press without looking.
 */

export interface MiraAcceptanceProps {
  readonly task: AiTask
  readonly proposed: unknown
  readonly model: string
  readonly modelVersion: string
  readonly confidence?: number
  /** True while the answer is being written, so a second confirm cannot be raised. */
  readonly busy: boolean
  readonly onConfirm: (answer: ComposedAnswer) => void
}

/**
 * A literal lookup, not a template, so the catalogue test can see each key is used.
 *
 * Keyed by what `AI-010`'s schema calls the value (`sph`, `cyl`), named in the message by what the
 * record calls it. The two spellings are what this screen and the prompt disagreed about.
 */
const RX_LABELS: Readonly<Record<string, MessageKey>> = {
  'od.sph': 'mira.rx.od.sphere',
  'od.cyl': 'mira.rx.od.cylinder',
  'od.axis': 'mira.rx.od.axis',
  'od.add': 'mira.rx.od.add',
  'os.sph': 'mira.rx.os.sphere',
  'os.cyl': 'mira.rx.os.cylinder',
  'os.axis': 'mira.rx.os.axis',
  'os.add': 'mira.rx.os.add',
}

/**
 * A label for each `MIRA_CONTEXT_FIELDS` pattern. Keyed by the pattern and not by the path, so one
 * entry names every element of `warnings`.
 *
 * A literal lookup for the reason `RX_LABELS` is one: the catalogue test can see each key is used.
 * `mira-acceptance.test.ts` fails if a pattern in the table has no entry here, so growing the table
 * cannot quietly put an English path fragment on an Urdu till.
 */
export const READ_LABELS: Readonly<Record<string, MessageKey>> = {
  readable: 'mira.read.readable',
  script: 'mira.read.script',
  writing: 'mira.read.writing',
  notes: 'mira.read.notes',
  'od.legibility': 'mira.read.legibilityOd',
  'os.legibility': 'mira.read.legibilityOs',
  raw_text: 'mira.read.rawText',
  'unparsed_rows.*': 'mira.read.unparsedRow',
  'warnings.*': 'mira.read.warning',
}

const PROBLEM_KEYS: Readonly<Record<FieldProblem, MessageKey>> = {
  dioptre: 'mira.field.invalid.dioptre',
  axis: 'mira.field.invalid.axis',
  number: 'mira.field.invalid.number',
  empty: 'mira.field.invalid.empty',
}

/**
 * `RX-03`: a power always carries its sign. Anything that is not a valid power is shown as given.
 *
 * `AI-010` returns a power as the slip's own string, so the sign may be missing even though the
 * number is a real power. Parsing and reformatting puts the sign back; a value that does not parse,
 * or sits off the 0.25 grid, is shown exactly as the model read it, because the person is being
 * asked to check a reading and a tidied-up version of a wrong one is harder to catch.
 */
function shown(task: AiTask, field: ProposalField): string {
  const position = rxPosition(task, field.path)
  if (position === null || isRxReadAxis(position.column)) return String(field.value)
  try {
    return formatDioptre(dioptre(rxNumber(field.value)))
  } catch {
    return String(field.value)
  }
}

/**
 * Whether the value is shown as a number: in an `Ltr` island, with a decimal keypad.
 *
 * A prescription value counts even though it arrives as a string. CLAUDE.md: RTL mirrors the layout
 * but never the numbers or the dioptric signs, and `-3.25` reordered by the bidi algorithm beside
 * Urdu text is a different prescription.
 */
const isNumeric = (task: AiTask, field: ProposalField): boolean =>
  typeof field.value === 'number' || rxPosition(task, field.path) !== null

export function MiraAcceptance({
  task,
  proposed,
  model,
  modelVersion,
  confidence,
  busy,
  onConfirm,
}: MiraAcceptanceProps): JSX.Element {
  const { t } = useTranslation()
  const prefix = useId()
  const all = proposalFields(proposed)
  const fields = decisionFields(task, all)
  const read = contextFields(task, all)
  const [decisions, setDecisions] = useState<Decisions>({})

  const decide = (id: string, decision: Decision): void => {
    setDecisions((previous) => ({ ...previous, [id]: decision }))
  }

  const remaining = undecidedCount(task, fields, decisions)
  const ready = remaining === 0 && !busy

  const confirm = (): void => {
    // The same two refusals the attribute describes. A click that arrives while something is
    // undecided, from a test, a script or a stale tab order, does nothing.
    if (!ready) return
    try {
      onConfirm(composeAnswer(task, proposed, decisions))
    } catch (error) {
      if (!(error instanceof NotDecided)) throw error
    }
  }

  return (
    <div className="lm-mira__proposal">
      <h2 className="lm-mira__subheading">{t('mira.proposal.heading')}</h2>
      <p className="lm-hint">{t('mira.proposal.notSaved')}</p>
      <p className="lm-hint">
        {t('mira.proposal.source', { model, version: modelVersion })}
        {confidence === undefined
          ? null
          : ` ${t('mira.proposal.confidence', { percent: Math.round(confidence * 100) })}`}
      </p>

      {read.length === 0 ? null : (
        <div className="lm-mira__read">
          <h3 className="lm-mira__subheading">{t('mira.read.heading')}</h3>
          <p className="lm-hint">{t('mira.read.hint')}</p>
          <dl className="lm-mira__readList">
            {read.map((field) => {
              const pattern = contextPatternOf(task, field.path)
              const labelKey = pattern === null ? undefined : READ_LABELS[pattern]
              return (
                <div key={field.id}>
                  <dt>{labelKey === undefined ? field.path.join(' ') : t(labelKey)}</dt>
                  <dd dir="auto">{String(field.value)}</dd>
                </div>
              )
            })}
          </dl>
        </div>
      )}

      {fields.map((field) => {
        const name = `${prefix}-${field.id}`
        const decision = decisions[field.id]
        const state = fieldState(task, field, decision)
        const position = rxPosition(task, field.path)
        const labelKey =
          position === null ? undefined : RX_LABELS[`${position.eye}.${position.column}`]
        const label =
          labelKey !== undefined
            ? t(labelKey)
            : field.path.length === 0
              ? t('mira.field.answer')
              : field.path.join(' ')
        const describedBy = `${name}-state`
        return (
          <fieldset key={field.id} className="lm-mira__field" aria-describedby={describedBy}>
            <legend className="lm-mira__legend">{label}</legend>
            <p className="lm-mira__value">
              <span className="lm-hint">{t('mira.field.mira')}</span>{' '}
              {isNumeric(task, field) ? (
                <Ltr className="lm-mira__number">{shown(task, field)}</Ltr>
              ) : (
                <span dir="auto">{shown(task, field)}</span>
              )}
            </p>
            <div className="lm-mira__choices">
              {(
                [
                  ['accept', 'mira.field.accept'],
                  ['edit', 'mira.field.edit'],
                  ['reject', 'mira.field.reject'],
                ] as const
              ).map(([kind, key]) => (
                <label key={kind} className="lm-mira__choice">
                  <input
                    type="radio"
                    name={name}
                    checked={decision?.kind === kind}
                    disabled={busy}
                    onChange={() => {
                      decide(
                        field.id,
                        kind === 'edit' ? { kind, text: String(field.value) } : { kind },
                      )
                    }}
                  />{' '}
                  {t(key)}
                </label>
              ))}
            </div>
            {decision?.kind === 'edit' ? (
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('mira.field.editLabel')}</span>
                <input
                  className="lm-input"
                  type="text"
                  inputMode={isNumeric(task, field) ? 'decimal' : 'text'}
                  dir={isNumeric(task, field) ? 'ltr' : 'auto'}
                  value={decision.text}
                  disabled={busy}
                  onChange={(event) => {
                    decide(field.id, { kind: 'edit', text: event.currentTarget.value })
                  }}
                />
              </label>
            ) : null}
            <p
              id={describedBy}
              className={state.kind === 'invalid' ? 'lm-error' : 'lm-hint'}
              role={state.kind === 'invalid' ? 'alert' : undefined}
            >
              {state.kind === 'invalid'
                ? t(PROBLEM_KEYS[state.problem])
                : state.kind === 'rejected'
                  ? t('mira.field.rejected')
                  : state.kind === 'undecided'
                    ? t('mira.field.undecided')
                    : null}
            </p>
          </fieldset>
        )
      })}

      <div className="lm-screen__actions">
        <button
          type="button"
          className="lm-button lm-button--primary"
          aria-disabled={!ready}
          onClick={confirm}
        >
          {t('mira.confirm')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          disabled={busy}
          onClick={() => {
            setDecisions(Object.fromEntries(fields.map((f) => [f.id, { kind: 'reject' } as const])))
          }}
        >
          {t('mira.rejectAll')}
        </button>
        <p className="lm-hint" role="status">
          {remaining > 0
            ? t('mira.confirm.blocked', { count: remaining })
            : busy
              ? t('mira.recording')
              : t('mira.confirm.ready')}
        </p>
      </div>
    </div>
  )
}
